/**
 * This renders some input text written using a subset of markdown supporting:
 * - paragraphs
 * - headings (shown as bold paragraphs)
 * - block quotes
 * - bulleted and numbered lists
 * - bold text
 * - italic text
 */
import * as preact from "preact";

interface Props {
  text: string;
}

type Block =
  | { type: "paragraph" | "heading" | "quote"; text: string }
  | { type: "list"; ordered: boolean; start: number; items: string[] };

const renderInline = (text: string) =>
  text
    .split("**")
    .map((boldChunk, boldChunkIndex) =>
      boldChunkIndex % 2 === 0 ? (
        boldChunk
          .split("*")
          .map((italicChunk, italicIndex) =>
            italicIndex % 2 === 0 ? italicChunk : <i>{italicChunk}</i>
          )
      ) : (
        <b>{boldChunk}</b>
      )
    );

/**
 * @returns the text's lines grouped into blocks, with consecutive list items
 *   of the same kind in one list
 */
const parseBlocks = (text: string): Block[] => {
  const blocks: Block[] = [];
  for (const line of text.split("\n")) {
    const heading = /^#{1,6}\s+(.*)$/.exec(line);
    const quote = /^>\s?(.*)$/.exec(line);
    const listItem = /^\s*(?:([-*•])|(\d+)[.)])\s+(.*)$/.exec(line);
    if (heading) {
      blocks.push({ type: "heading", text: heading[1] });
    } else if (quote) {
      blocks.push({ type: "quote", text: quote[1] });
    } else if (listItem) {
      const ordered = !listItem[1];
      const previous = blocks[blocks.length - 1];
      if (previous?.type === "list" && previous.ordered === ordered) {
        previous.items.push(listItem[3]);
      } else {
        // Keep the model's numbering when a blank line splits a numbered list
        const start = ordered ? Number(listItem[2]) : 1;
        blocks.push({ type: "list", ordered, start, items: [listItem[3]] });
      }
    } else {
      blocks.push({ type: "paragraph", text: line });
    }
  }
  return blocks;
};

const renderBlock = (block: Block) => {
  if (block.type === "list") {
    const items = block.items.map((item) => <li>{renderInline(item)}</li>);
    return block.ordered ? (
      <ol start={block.start === 1 ? undefined : block.start}>{items}</ol>
    ) : (
      <ul>{items}</ul>
    );
  }
  if (block.type === "heading") {
    return (
      <p>
        <b>{renderInline(block.text)}</b>
      </p>
    );
  }
  if (block.type === "quote") {
    return <blockquote>{renderInline(block.text)}</blockquote>;
  }
  return <p>{renderInline(block.text)}</p>;
};

class MiniMarkdownView extends preact.Component<Props> {
  render() {
    return (
      <div class="miniMarkdown">
        {parseBlocks(this.props.text).map(renderBlock)}
      </div>
    );
  }
}

export default MiniMarkdownView;
